/**
 * 1.3.0 the portal app's API client (stream P3's file): /api/v1/portal/* on the portal's own origin, with its own
 * session cookie (credentials 'same-origin'), JSON in and out, errors as PortalApiError with the API's code.
 */
import type { PortalMeDTO } from '@prism/contracts';
import { t } from './i18n/en';
import type { PortalSession } from './types';
import { quotaErrorText } from './extras/quota';

export class PortalApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function portalApi<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api/v1/portal${path}`, {
    ...rest,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) {
    const e = body?.error ?? {};
    const err = new PortalApiError(res.status, e.code ?? 'ERROR', e.message ?? res.statusText, e.details);
    if (res.status === 401) window.dispatchEvent(new CustomEvent('portal:signed-out'));
    throw err;
  }
  return body as T;
}

/** What to tell the supplier about an error: the catalogue's text for its code, else the API's message. */
export function errorText(err: unknown): string {
  if (err instanceof PortalApiError) {
    // 1.3.1 (Q1, extras/quota.ts): a refused upload's quota, with what is used and the limit.
    const quota = quotaErrorText(err);
    if (quota) return quota;
    for (const key of [`portal.error.${err.code}`, `error.${err.code}`]) {
      const s = t(key);
      if (s !== key) return err.code === 'NAMING_RULE' || err.code === 'UNSUPPORTED_TYPE' || err.code === 'UPLOAD_TOO_LARGE' ? err.message : s;
    }
    return err.message;
  }
  return t('common.error');
}

/** Who is signed in: P1's GET /me (401: signed out). */
export async function loadSession(): Promise<PortalSession | 'signed-out'> {
  try {
    const me = await portalApi<PortalMeDTO>('/me');
    return { me, branding: me.branding, workspaceName: me.workspaceName };
  } catch (err) {
    // Signed out, or the portal is off (the sign-in screen says so from /public).
    if (err instanceof PortalApiError && (err.status === 401 || err.code === 'PORTAL_DISABLED')) return 'signed-out';
    throw err;
  }
}
